import React, { useState, useEffect } from 'react';
import { useLanguage, languages } from '../contexts/LanguageContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';

export function Navbar() {
  const { t, currentLanguage, changeLanguage } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (sectionId, isPage = false) => {
    setIsMobileMenuOpen(false);
    
    if (isPage) {
      navigate(`/${sectionId}`);
      return;
    }

    if (!isHomePage) {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const navItems = [
    { key: 'expertise', id: 'expertise', isPage: false },
    { key: 'caseStudies', id: 'case-studies', isPage: true },
    { key: 'insights', id: 'insights', isPage: true },
    { key: 'about', id: 'about', isPage: true },
    { key: 'contact', id: 'contact', isPage: true }
  ];

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-5 left-1/2 -translate-x-1/2 w-[94%] max-w-4xl z-50 transition-all duration-500 ease-premium ${
        isScrolled
          ? 'bg-black/60 backdrop-blur-2xl border-white/[0.08]'
          : 'bg-black/30 backdrop-blur-xl border-white/[0.05]'
      } border rounded-xl px-5 md:px-6 py-3`}
    >
      <div className="flex items-center justify-between">
        {/* Logo - Clean, minimal */}
        <Link
          to="/"
          data-testid="nav-logo"
          className="text-lg font-semibold text-[#E6EDF3] hover:text-[#1C7C7D] transition-colors duration-300 tracking-tight"
        >
          DATEX
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            item.isPage ? (
              <Link
                key={item.key}
                to={`/${item.id}`}
                data-testid={`nav-${item.id}`}
                className={`text-sm font-medium transition-colors duration-300 ${
                  location.pathname.includes(item.id) 
                    ? 'text-[#14B8A6]' 
                    : 'text-white/80 hover:text-white'
                }`}
              >
                {t(`nav.${item.key}`)}
              </Link>
            ) : (
              <button
                key={item.key}
                onClick={() => handleNavClick(item.id, item.isPage)}
                data-testid={`nav-${item.id}`}
                className="text-sm font-medium text-white/80 hover:text-white transition-colors duration-300"
              >
                {t(`nav.${item.key}`)}
              </button>
            )
          ))}
        </div>

        {/* Language Switcher */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <button
              onClick={() => setIsLangMenuOpen(!isLangMenuOpen)}
              data-testid="language-switcher"
              className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-white/70 hover:text-white transition-colors duration-300 bg-white/[0.05] hover:bg-white/[0.1] rounded-md border border-white/[0.1] hover:border-white/15"
            >
              <Globe className="w-3.5 h-3.5" />
              <span>{currentLanguage.toUpperCase()}</span>
              <ChevronDown className={`w-3 h-3 transition-transform duration-300 ${isLangMenuOpen ? 'rotate-180' : ''}`} />
            </button>

            {isLangMenuOpen && (
              <div 
                className="absolute top-full right-0 mt-2 bg-[#11161C] backdrop-blur-2xl border border-white/[0.08] rounded-lg overflow-hidden min-w-[120px] shadow-2xl"
                data-testid="language-dropdown"
              >
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      changeLanguage(lang.code);
                      setIsLangMenuOpen(false);
                    }}
                    data-testid={`lang-${lang.code}`}
                    className={`w-full px-4 py-2.5 text-left text-xs font-medium hover:bg-white/[0.05] transition-colors duration-200 flex items-center gap-2 ${
                      currentLanguage === lang.code ? 'text-[#14B8A6] bg-white/[0.03]' : 'text-white/70'
                    }`}
                  >
                    <span className="opacity-60">{lang.flag}</span>
                    <span>{lang.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 text-white/70 hover:text-white transition-colors duration-300"
          >
            {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div 
          className="md:hidden mt-4 pt-4 border-t border-white/[0.06]"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-1">
            {navItems.map((item) => (
              item.isPage ? (
                <Link
                  key={item.key}
                  to={`/${item.id}`}
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`mobile-nav-${item.id}`}
                  className="px-3 py-2.5 text-sm font-medium text-white/70 hover:text-white hover:bg-white/[0.05] rounded-md transition-colors duration-200"
                >
                  {t(`nav.${item.key}`)}
                </Link>
              ) : (
                <button
                  key={item.key}
                  onClick={() => handleNavClick(item.id, item.isPage)}
                  data-testid={`mobile-nav-${item.id}`}
                  className="px-3 py-2.5 text-left text-sm font-medium text-white/70 hover:text-white hover:bg-white/[0.05] rounded-md transition-colors duration-200"
                >
                  {t(`nav.${item.key}`)}
                </button>
              )
            ))}
          </div>
        </div>
      )}
    </nav>
  );
}
